import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAuth } from "@/context/AuthContext";
import usePermissions from "@/lib/usePermissions";
import { ChevronLeft, ChevronRight, CalendarDays, Plus, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                    */
/* -------------------------------------------------------------------------- */

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function toIso(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function monthLabel(yyyy_mm) {
  const [y, m] = yyyy_mm.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleString(undefined, { month: "long", year: "numeric" });
}

function shiftMonth(yyyy_mm, by) {
  const [y, m] = yyyy_mm.split("-").map(Number);
  const d = new Date(y, m - 1 + by, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/** Build a 6-week grid starting on Monday so the calendar always has the same
 *  height regardless of which month it lands in (28-day Feb gets 4 rows of
 *  events + 2 padded rows = stable layout). */
function buildGrid(yyyy_mm) {
  const [y, m] = yyyy_mm.split("-").map(Number);
  const first = new Date(y, m - 1, 1);
  // Convert Sun(0)..Sat(6) to Mon(0)..Sun(6) so weekends sit on the right edge.
  const weekdayOfFirst = (first.getDay() + 6) % 7;
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() - weekdayOfFirst);
  const cells = [];
  for (let i = 0; i < 42; i += 1) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    cells.push({
      date: d,
      iso: toIso(d),
      inMonth: d.getMonth() === first.getMonth(),
      isWeekend: d.getDay() === 0 || d.getDay() === 6,
    });
  }
  return cells;
}

/* -------------------------------------------------------------------------- */
/*  Holiday manager (admin-only)                                               */
/* -------------------------------------------------------------------------- */

function HolidayManager({ onChange }) {
  const [open, setOpen] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [draft, setDraft] = useState({ date: "", name: "", type: "public", description: "" });
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/leaves/holidays", { params: { year } });
      setHolidays(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { if (open) load(); }, [open, year]);

  const save = async () => {
    if (!draft.date || !draft.name.trim()) {
      toast.error("Date and name are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/leaves/holidays", {
        date: draft.date,
        name: draft.name.trim(),
        type: draft.type || "public",
        description: draft.description.trim() || null,
      });
      toast.success("Holiday added");
      setDraft({ date: "", name: "", type: "public", description: "" });
      await load();
      onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (h) => {
    setRemoving((m) => ({ ...m, [h.id]: true }));
    try {
      await api.delete(`/leaves/holidays/${h.id}`);
      setHolidays((rows) => rows.filter((x) => x.id !== h.id));
      onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRemoving((m) => { const { [h.id]: _omit, ...rest } = m; return rest; }); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-sm h-8" data-testid="manage-holidays-btn">
          <CalendarDays size={13} className="mr-1.5" />Manage holidays
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="holiday-manager-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Sparkles size={16} className="text-orange-500" /> Public holiday calendar
          </DialogTitle>
          <DialogDescription>
            Holidays you add here suppress the check-in nudge for everyone, surface on the team leave calendar, and act as a hint when people apply for leave.
          </DialogDescription>
        </DialogHeader>

        {/* Add new */}
        <div className="grid grid-cols-12 gap-2 items-end p-3 bg-slate-50 rounded-sm">
          <div className="col-span-3">
            <Label className="label-overline">Date</Label>
            <Input type="date" className="rounded-sm mt-1" value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} data-testid="holiday-date-input" />
          </div>
          <div className="col-span-4">
            <Label className="label-overline">Name</Label>
            <Input className="rounded-sm mt-1" placeholder="e.g. Republic Day" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="holiday-name-input" />
          </div>
          <div className="col-span-2">
            <Label className="label-overline">Type</Label>
            <Select value={draft.type} onValueChange={(v) => setDraft({ ...draft, type: v })}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="holiday-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="public">Public</SelectItem>
                <SelectItem value="company">Company</SelectItem>
                <SelectItem value="optional">Optional</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-3">
            <Button onClick={save} disabled={saving} className="w-full rounded-sm bg-slate-900 hover:bg-slate-800 text-white" data-testid="holiday-add-btn">
              <Plus size={12} className="mr-1.5" /> {saving ? "Adding…" : "Add holiday"}
            </Button>
          </div>
          <div className="col-span-12">
            <Input className="rounded-sm" placeholder="Optional description — e.g. 'Office closed' / 'Optional, marked half-day'" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} data-testid="holiday-desc-input" />
          </div>
        </div>

        {/* Year switch + list */}
        <div className="mt-4 flex items-center gap-2 justify-between">
          <div className="label-overline">{holidays.length} holiday{holidays.length === 1 ? "" : "s"} in {year}</div>
          <div className="inline-flex items-center gap-1">
            {[year - 1, year, year + 1].map((y) => (
              <button
                key={y}
                onClick={() => setYear(y)}
                className={`px-2 py-1 text-[11px] rounded-sm border transition-colors ${
                  y === year ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"
                }`}
                data-testid={`holiday-year-${y}`}
              >
                {y}
              </button>
            ))}
          </div>
        </div>

        <ul className="mt-2 divide-y divide-slate-100 border border-slate-200 rounded-sm overflow-hidden" data-testid="holiday-list">
          {holidays.length === 0 && (
            <li className="px-3 py-6 text-center text-xs text-slate-400 italic">No holidays for {year} yet.</li>
          )}
          {holidays.map((h) => (
            <li key={h.id} className="flex items-center gap-3 px-3 py-2 text-sm hover:bg-slate-50" data-testid={`holiday-row-${h.id}`}>
              <span className="font-mono text-xs text-slate-500 w-24 shrink-0">{h.date}</span>
              <span className="flex-1 text-slate-800">{h.name}</span>
              <span className={`px-1.5 py-0.5 rounded-sm text-[10px] uppercase tracking-wider ${
                h.type === "company" ? "bg-purple-100 text-purple-700" : h.type === "optional" ? "bg-slate-100 text-slate-600" : "bg-orange-100 text-orange-700"
              }`}>{h.type || "public"}</span>
              <button
                onClick={() => remove(h)}
                disabled={!!removing[h.id]}
                className="text-slate-400 hover:text-red-600 p-1 rounded-sm hover:bg-red-50"
                aria-label="Remove holiday"
                data-testid={`holiday-remove-${h.id}`}
              >
                <Trash2 size={12} />
              </button>
            </li>
          ))}
        </ul>
        <DialogFooter><Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------------------------------------------------------------- */
/*  Calendar grid                                                              */
/* -------------------------------------------------------------------------- */

export default function LeavesCalendar() {
  const can = usePermissions();
  const isAdmin = can("leaves_edit");
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [feed, setFeed] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (m) => {
    setLoading(true);
    try {
      const { data } = await api.get("/leaves/calendar", { params: { month: m } });
      setFeed(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(month); }, [month]);

  const grid = useMemo(() => buildGrid(month), [month]);
  const todayIso = toIso(new Date());

  // Pre-compute a flat list of "out this month" people for the right-side rail.
  const peopleOut = useMemo(() => {
    if (!feed?.days) return [];
    const byPerson = {};
    Object.entries(feed.days).forEach(([d, dayData]) => {
      (dayData.leaves || []).forEach((lv) => {
        const k = lv.user_id || lv.user_name;
        if (!byPerson[k]) byPerson[k] = { user_name: lv.user_name, leave_type: lv.leave_type, color: lv.color, dates: [] };
        byPerson[k].dates.push(d);
      });
    });
    return Object.values(byPerson).sort((a, b) => a.user_name.localeCompare(b.user_name));
  }, [feed]);

  return (
    <div className="card-flat overflow-hidden mb-6" data-testid="leaves-calendar">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 bg-slate-50/50">
        <div className="inline-flex items-center gap-2">
          <CalendarDays size={14} className="text-slate-500" />
          <span className="font-display font-semibold text-slate-800 text-sm">Team calendar</span>
          <span className="text-xs text-slate-400">· public holidays + who&apos;s out</span>
        </div>
        <div className="inline-flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setMonth(shiftMonth(month, -1))} className="h-7 w-7 p-0 rounded-sm" data-testid="cal-prev"><ChevronLeft size={14} /></Button>
          <div className="font-medium text-sm text-slate-700 min-w-[150px] text-center" data-testid="cal-month-label">{monthLabel(month)}</div>
          <Button variant="ghost" size="sm" onClick={() => setMonth(shiftMonth(month, +1))} className="h-7 w-7 p-0 rounded-sm" data-testid="cal-next"><ChevronRight size={14} /></Button>
          <Button variant="outline" size="sm" onClick={() => {
            const d = new Date();
            setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
          }} className="h-7 rounded-sm text-xs" data-testid="cal-today">Today</Button>
          {isAdmin && <HolidayManager onChange={() => load(month)} />}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row">
        {/* Calendar grid */}
        <div className="flex-1 p-2">
          <div className="grid grid-cols-7 gap-px bg-slate-100">
            {WEEKDAY_LABELS.map((w) => (
              <div key={w} className={`bg-white text-[10px] uppercase tracking-wider text-center py-1.5 ${w === "Sat" || w === "Sun" ? "text-slate-400" : "text-slate-500"}`}>{w}</div>
            ))}
            {grid.map((cell) => {
              const dayData = feed?.days?.[cell.iso] || { holiday: null, leaves: [] };
              const isToday = cell.iso === todayIso;
              const visibleLeaves = (dayData.leaves || []).slice(0, 3);
              const overflow = (dayData.leaves || []).length - visibleLeaves.length;
              const baseBg = !cell.inMonth ? "bg-slate-50/50" : dayData.holiday ? "bg-orange-50" : cell.isWeekend ? "bg-slate-50" : "bg-white";
              return (
                <Popover key={cell.iso}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className={`${baseBg} min-h-[78px] p-1.5 text-left transition-colors hover:bg-slate-100/70 group ${dayData.holiday ? "ring-1 ring-orange-200" : ""}`}
                      data-testid={`cal-day-${cell.iso}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className={`text-xs font-mono ${
                          !cell.inMonth ? "text-slate-300" :
                          isToday ? "bg-slate-900 text-white px-1.5 rounded-sm font-semibold" :
                          dayData.holiday ? "text-orange-700 font-semibold" :
                          cell.isWeekend ? "text-slate-400" :
                          "text-slate-600"
                        }`}>
                          {cell.date.getDate()}
                        </span>
                        {dayData.holiday && (
                          <span className="inline-block w-1.5 h-1.5 rounded-full bg-orange-500" aria-hidden="true" />
                        )}
                      </div>
                      {dayData.holiday && (
                        <div className="mt-1 text-[10px] text-orange-700 font-semibold truncate" data-testid={`cal-holiday-${cell.iso}`}>{dayData.holiday.name}</div>
                      )}
                      <ul className="mt-1 space-y-0.5">
                        {visibleLeaves.map((lv) => (
                          <li key={lv.id + "-" + cell.iso} className="text-[10px] truncate flex items-center gap-1">
                            <span className="inline-block w-1.5 h-1.5 rounded-full shrink-0" style={{ background: lv.color }} aria-hidden="true" />
                            <span className="text-slate-700 truncate">{lv.user_name.split(" ")[0]}{lv.half_day ? " ½" : ""}</span>
                          </li>
                        ))}
                        {overflow > 0 && <li className="text-[10px] text-slate-400">+{overflow} more</li>}
                      </ul>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 rounded-sm" data-testid={`cal-day-popover-${cell.iso}`}>
                    <div className="text-xs font-semibold text-slate-800 mb-1">
                      {cell.date.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}
                    </div>
                    {dayData.holiday && (
                      <div className="text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded-sm p-2 mb-2">
                        <div className="font-semibold">{dayData.holiday.name}</div>
                        <div className="text-[10px] uppercase tracking-wider opacity-70 mt-0.5">{dayData.holiday.type}</div>
                        {dayData.holiday.description && <div className="text-[11px] text-orange-600 mt-1">{dayData.holiday.description}</div>}
                      </div>
                    )}
                    {(dayData.leaves || []).length > 0 ? (
                      <ul className="space-y-1.5" data-testid={`cal-popover-leaves-${cell.iso}`}>
                        {(dayData.leaves || []).map((lv) => (
                          <li key={lv.id + "-pop-" + cell.iso} className="flex items-center gap-2 text-xs">
                            <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ background: lv.color }} />
                            <span className="text-slate-800 font-medium flex-1">{lv.user_name}</span>
                            <span className="text-[10px] text-slate-500 capitalize">{lv.leave_type}{lv.half_day ? " · ½" : ""}</span>
                          </li>
                        ))}
                      </ul>
                    ) : !dayData.holiday ? (
                      <div className="text-[11px] text-slate-400 italic">Nobody on leave.</div>
                    ) : null}
                  </PopoverContent>
                </Popover>
              );
            })}
          </div>
        </div>

        {/* Right rail — who's out this month */}
        <aside className="lg:w-60 lg:border-l border-slate-100 p-3 lg:max-h-[440px] lg:overflow-y-auto bg-slate-50/30">
          <div className="label-overline mb-2">Out this month ({peopleOut.length})</div>
          {loading && <div className="text-xs text-slate-400">Loading…</div>}
          {!loading && peopleOut.length === 0 && <div className="text-xs text-slate-400 italic">Full house — nobody on approved leave.</div>}
          <ul className="space-y-1.5">
            {peopleOut.map((p, i) => (
              <li key={`${p.user_name}-${i}`} className="flex items-center gap-2 text-xs">
                <span className="inline-block w-2.5 h-2.5 rounded-full shrink-0" style={{ background: p.color }} />
                <span className="text-slate-800 flex-1 truncate">{p.user_name}</span>
                <span className="text-slate-400">{p.dates.length}d</span>
              </li>
            ))}
          </ul>

          {(feed?.holidays || []).length > 0 && (
            <>
              <div className="label-overline mt-4 mb-2">Holidays this month</div>
              <ul className="space-y-1" data-testid="cal-month-holidays">
                {feed.holidays.map((h) => (
                  <li key={h.id} className="text-xs flex items-center gap-2">
                    <span className="inline-block w-2 h-2 rounded-full bg-orange-500 shrink-0" />
                    <span className="font-mono text-slate-500 text-[10px]">{h.date.slice(5)}</span>
                    <span className="text-slate-700 truncate flex-1">{h.name}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </aside>
      </div>
    </div>
  );
}
